import { gql, useQuery } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import NextLink from 'next/link';
import * as React from 'react';

import { GetUnassignedMembershipInvoicesInput } from '@/graphql-types/globalTypes';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';

import {
  UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices,
  UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoicesVariables,
  UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices_getUnassignedMembershipInvoices_invoices,
} from './graphql-types/UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices';

const GET_UNASSIGNED_MEMBERSHIP_INVOICES = gql`
  query UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices(
    $input: GetUnassignedMembershipInvoicesInput!
  ) {
    getUnassignedMembershipInvoices(input: $input) {
      invoices {
        _id
        companyId
        createdAt
        discount
        dueAmount
        dueDate
        invoiceCategory
        invoiceNumber
        isDraft
        receivedAmount
        source
        status
        subTotal
        tenantId
        total
        userId
        memberships {
          discount
          isNewMembership
          isVoided
          quantity
          subTotal
          total
          unitPrice
          membershipTypeId {
            _id
            name
            type
            validityMonths
          }
        }
      }
    }
  }
`;

export interface UnassignedMembershipInvoicesAlertProps {
  userId?: string | null;
  organizationId?: string | null;
}

const formatInvoiceNumber = (
  invoice: UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices_getUnassignedMembershipInvoices_invoices
) => invoice.invoiceNumber || `INV-${invoice._id.slice(-8)}`;

const getMembershipNames = (
  invoice: UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices_getUnassignedMembershipInvoices_invoices
) => {
  const names =
    invoice.memberships?.flatMap((membership) => {
      if (membership.isVoided || !membership.membershipTypeId?.name) {
        return [];
      }

      return [membership.membershipTypeId.name];
    }) ?? [];

  return names.length > 0 ? names.join(', ') : 'Membership';
};

const formatCurrencyAmount = (amount: number, currencyCode: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);

const formatDueDate = (dueDate: string | null) =>
  dueDate ? dayjs(dueDate).format('MMM D, YYYY') : 'Not set';

const isPastDue = (dueDate: string | null) =>
  !!dueDate && dayjs(dueDate).isBefore(dayjs(), 'day');

const UnassignedInvoiceAlertItem: React.FC<{
  invoice: UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices_getUnassignedMembershipInvoices_invoices;
  currencyCode: string;
}> = ({ invoice, currencyCode }) => {
  const pastDue = isPastDue(invoice.dueDate);

  return (
    <Alert
      status="warning"
      borderRadius="md"
      alignItems="flex-start"
      bg="orange.50"
      border="1px solid"
      borderColor="orange.200"
    >
      <AlertIcon mt={0.5} />
      <Box minW={0} flex="1">
        <AlertDescription>
          <Stack spacing={1}>
            <Text fontWeight="semibold" color="textPrimary.500">
              Pending assignment of unpaid membership invoice
            </Text>
            <Text fontSize="sm" color="textSecondary.500">
              Invoice Number:{' '}
              <Text as="span" fontWeight="medium" color="textPrimary.500">
                {formatInvoiceNumber(invoice)}
              </Text>
            </Text>
            <Text fontSize="sm" color="textSecondary.500">
              Membership:{' '}
              <Text as="span" fontWeight="medium" color="textPrimary.500">
                {getMembershipNames(invoice)}
              </Text>
            </Text>
            <Text fontSize="sm" color="textSecondary.500">
              Amount Due:{' '}
              <Text as="span" fontWeight="medium" color="textPrimary.500">
                {formatCurrencyAmount(invoice.dueAmount, currencyCode)}
              </Text>
            </Text>
            <Text fontSize="sm" color="textSecondary.500">
              Due Date:{' '}
              <Text
                as="span"
                fontWeight="medium"
                color={pastDue ? 'orange.600' : 'textPrimary.500'}
              >
                {formatDueDate(invoice.dueDate)}
              </Text>
            </Text>
            <Link
              as={NextLink}
              href={`/me/invoices?invoiceId=${invoice._id}`}
              fontSize="sm"
              fontWeight="semibold"
              color="primary.600"
            >
              View and pay invoice
            </Link>
          </Stack>
        </AlertDescription>
      </Box>
    </Alert>
  );
};

export const UnassignedMembershipInvoicesAlert: React.FC<
  UnassignedMembershipInvoicesAlertProps
> = ({ userId = null, organizationId = null }) => {
  const { currency } = useDefaultCurrency();
  const shouldSkipQuery = !userId && !organizationId;

  const { data, loading, error } = useQuery<
    UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoices,
    UnassignedMembershipInvoicesAlert_GetUnassignedMembershipInvoicesVariables
  >(GET_UNASSIGNED_MEMBERSHIP_INVOICES, {
    variables: {
      input: {
        ...(userId != null && { userId }),
        ...(organizationId != null && { organizationId }),
      } as GetUnassignedMembershipInvoicesInput,
    },
    skip: shouldSkipQuery,
    fetchPolicy: 'cache-and-network',
  });

  if (shouldSkipQuery || loading || error) {
    return null;
  }

  const invoices = data?.getUnassignedMembershipInvoices?.invoices ?? [];

  if (invoices.length === 0) {
    return null;
  }

  return (
    <Stack spacing={3} mb={4}>
      {invoices.map((invoice) => (
        <UnassignedInvoiceAlertItem
          key={invoice._id}
          invoice={invoice}
          currencyCode={currency.code}
        />
      ))}
    </Stack>
  );
};
